import client from './client';
import {
  AudienceCount,
  BroadcastCampaign,
  BroadcastGroup,
  BroadcastGroupDetail,
  CustomRecipient,
  BroadcastSegment,
  CampaignRecipient,
  CreateBroadcastPayload,
  MetaTemplate,
} from '../types/broadcast';
import { ListResult, normalizeList } from '../utils/list';

export async function getTemplates(): Promise<MetaTemplate[]> {
  const res = await client.get('/api/admin/whatsapp/templates');
  return Array.isArray(res.data?.templates) ? res.data.templates : [];
}

export async function getAudienceCount(segment: BroadcastSegment): Promise<AudienceCount> {
  const res = await client.get('/api/admin/broadcasts/audience-count', { params: { segment } });
  const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
  return {
    count: num(res.data?.count) ?? 0,
    total: num(res.data?.total),
    without_phone: num(res.data?.without_phone),
  };
}

export async function getCampaigns(page = 1, limit = 20): Promise<ListResult<BroadcastCampaign>> {
  const res = await client.get('/api/admin/broadcasts', { params: { page, limit } });
  return normalizeList<BroadcastCampaign>(res.data, 'campaigns', limit);
}

export async function getCampaignRecipients(
  campaignId: string,
  page = 1,
  limit = 50,
  status = 'all'
): Promise<ListResult<CampaignRecipient>> {
  const res = await client.get(`/api/admin/broadcasts/${campaignId}/recipients`, {
    params: { page, limit, status },
  });
  return normalizeList<CampaignRecipient>(res.data, 'recipients', limit);
}

// Re-queue failed recipients. Pass recipientIds to retry a subset; omit to retry
// all failed recipients in the campaign.
export async function retryCampaign(
  campaignId: string,
  recipientIds?: string[]
): Promise<{ retried: number }> {
  const res = await client.post(`/api/admin/broadcasts/${campaignId}/retry`, {
    recipientIds: recipientIds ?? [],
  });
  return { retried: res.data?.retried ?? 0 };
}

export async function createBroadcast(
  payload: CreateBroadcastPayload
): Promise<{ campaignId: string; recipientCount: number }> {
  const res = await client.post('/api/admin/broadcasts', payload);
  return {
    campaignId: res.data?.campaignId,
    recipientCount: res.data?.recipientCount ?? 0,
  };
}

// ── Saved groups ────────────────────────────────────────────────
// Nested under /broadcasts so the gateway's existing proxy covers them.

export async function getGroups(): Promise<BroadcastGroup[]> {
  const res = await client.get('/api/admin/broadcasts/groups');
  return Array.isArray(res.data?.groups) ? res.data.groups : [];
}

export async function getGroup(groupId: string): Promise<BroadcastGroupDetail> {
  const res = await client.get(`/api/admin/broadcasts/groups/${groupId}`);
  return res.data.group;
}

export async function createGroup(payload: {
  name: string;
  description?: string | null;
  members: CustomRecipient[];
}): Promise<{ groupId: string; memberCount: number }> {
  const res = await client.post('/api/admin/broadcasts/groups', payload);
  return { groupId: res.data.groupId, memberCount: res.data.memberCount };
}

export async function renameGroup(groupId: string, name: string, description?: string | null): Promise<void> {
  await client.patch(`/api/admin/broadcasts/groups/${groupId}`, { name, description });
}

export async function addGroupMembers(
  groupId: string,
  members: CustomRecipient[]
): Promise<{ added: number; skipped: number }> {
  const res = await client.post(`/api/admin/broadcasts/groups/${groupId}/members`, { members });
  return { added: res.data.added ?? 0, skipped: res.data.skipped ?? 0 };
}

export async function removeGroupMember(groupId: string, memberId: string): Promise<void> {
  await client.delete(`/api/admin/broadcasts/groups/${groupId}/members/${memberId}`);
}

export async function deleteGroup(groupId: string): Promise<void> {
  await client.delete(`/api/admin/broadcasts/groups/${groupId}`);
}
